import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import i18next from '../../locales/i18n';

@customElement('bnb-hero')
export class BnbHero extends LitElement {
  static styles = css`
    :host {
      display: block;
      position: relative;
      height: 100vh;
      overflow: hidden;
    }

    .hero-container {
      position: relative;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .hero-background {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #1e3a8a 0%, #312e81 100%);
      z-index: 1;
    }

    .hero-image {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: url('/img/background/IMG_0608.webp');
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      z-index: 1;
    }

    .hero-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.6);
      z-index: 2;
    }

    .hero-content {
      position: relative;
      z-index: 3;
      text-align: center;
      color: white;
      max-width: 800px;
      padding: 0 2rem;
    }

    .hero-title {
      font-size: clamp(2.5rem, 6vw, 4rem);
      font-weight: 300;
      letter-spacing: 0.02em;
      margin: 0 0 1rem 0;
      line-height: 1.1;
    }

    .hero-subtitle {
      font-size: clamp(1.2rem, 3vw, 1.8rem);
      font-weight: 300;
      color: #00bcd4;
      margin: 0 0 2rem 0;
      letter-spacing: 0.1em;
    }

    .hero-description {
      font-size: 1.1rem;
      line-height: 1.6;
      margin: 0 0 3rem 0;
      font-weight: 300;
      opacity: 0.9;
      max-width: 600px;
      margin-left: auto;
      margin-right: auto;
    }

    .hero-navigation {
      display: flex;
      gap: 1rem;
      justify-content: center;
      flex-wrap: wrap;
    }

    .nav-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0.75rem 1.5rem;
      background: transparent;
      border: 2px solid rgba(255, 255, 255, 0.3);
      color: white;
      text-decoration: none;
      border-radius: 6px;
      font-weight: 300;
      font-size: 0.9rem;
      letter-spacing: 0.05em;
      transition: all 0.3s ease;
      cursor: pointer;
    }

    .nav-button:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: #00bcd4;
      transform: translateY(-2px);
    }

    .nav-button.primary {
      background: #00bcd4;
      border-color: #00bcd4;
    }

    .nav-button.primary:hover {
      background: #00acc1;
      border-color: #00acc1;
    }

    .scroll-indicator {
      position: absolute;
      bottom: 2rem;
      left: 50%;
      transform: translateX(-50%);
      z-index: 3;
      color: rgba(255, 255, 255, 0.7);
      font-size: 0.8rem;
      text-align: center;
      cursor: pointer;
    }

    .scroll-arrow {
      margin-top: 0.5rem;
      animation: bounce 2s infinite;
    }

    @keyframes bounce {
      0%, 20%, 50%, 80%, 100% {
        transform: translateY(0);
      }
      40% {
        transform: translateY(-10px);
      }
      60% {
        transform: translateY(-5px);
      }
    }

    @media (max-width: 768px) {
      .hero-content {
        padding: 0 1rem;
      }

      .hero-navigation {
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;
      }

      .nav-button {
        min-width: 200px;
      }
    }
  `;

  @state()
  private tagline = i18next.t('hero.tagline');

  private boundHandleLanguageChange = this.handleLanguageChange.bind(this);

  private handleLanguageChange() {
    this.tagline = i18next.t('hero.tagline');
    this.requestUpdate();
  }

  connectedCallback() {
    super.connectedCallback();
    // Listen for language changes
    i18next.on('languageChanged', this.boundHandleLanguageChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    i18next.off('languageChanged', this.boundHandleLanguageChange);
  }

  private handleScrollDown() {
    const aboutSection = document.querySelector('#about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  }

  render() {
    return html`
      <div class="hero-container">
        <div class="hero-background"></div>
        <div class="hero-image" aria-hidden="true"></div>
        <div class="hero-overlay"></div>

        <div class="hero-content">
          <h1 class="hero-title">Brand New Broadcast</h1>
          <p class="hero-description">${this.tagline}</p>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bnb-hero': BnbHero;
  }
}
